import type { BrandAnalyticsResponse } from "@/services/brand.service";
import { formatAmount, formatNumber } from "@/utils/formatters";
import { cn } from "@/lib/utils";
import { describeChange, MetricPanel, type Metric } from "@/components/dashboard/metric-cell";
import { PeriodToggle } from "@/components/dashboard/period-toggle";

export function ActivityStrip({
  analytics,
  periodDays,
  onPeriodChange,
  isRefreshing,
}: {
  analytics: BrandAnalyticsResponse | null;
  periodDays: number;
  onPeriodChange: (days: number) => void;
  isRefreshing: boolean;
}) {
  const revenue = analytics?.totalAffiliateRevenue;
  const orders = analytics?.totalAffiliateOrders;
  const payouts = analytics?.amountPayed;
  const codes = analytics?.codeUses;
  const affiliates = analytics?.totalAffilates;
  const postCheckout = analytics?.postCheckoutAffiliates;
  const n = formatNumber;

  const metrics: Metric[] = [
    {
      id: "dashboard-revenue-card",
      label: "Affiliate revenue",
      value: formatAmount(revenue?.totalRevenue ?? 0),
      change: describeChange(revenue?.totalRevenue ?? 0, revenue?.previusRevenue, periodDays, formatAmount),
      series: revenue?.revenueByDay,
      unit: "revenue",
      format: formatAmount,
    },
    {
      id: "dashboard-order-card",
      label: "Orders",
      value: n(orders?.totalOrders ?? 0),
      change: describeChange(orders?.totalOrders ?? 0, orders?.previusOrders, periodDays, n),
      series: orders?.ordersByDay,
      unit: "order",
    },
    {
      id: "dashboard-payout-card",
      label: "Paid out",
      value: formatAmount(payouts?.amountPayed ?? 0),
      change: describeChange(payouts?.amountPayed ?? 0, payouts?.previousPayedAmount, periodDays, formatAmount),
      series: payouts?.amountByDay,
      unit: "payout",
      format: formatAmount,
    },
    {
      id: "dashboard-discount-code-card",
      label: "Code uses",
      value: n(codes?.codeUses ?? 0),
      change: describeChange(codes?.codeUses ?? 0, codes?.previousCodeUses, periodDays, n),
      series: codes?.codeUsesByDay,
      unit: "use",
    },
    {
      id: "dashboard-map-total-affiliates-card",
      label: "New affiliates",
      value: n(affiliates?.currentTotals ?? 0),
      change: describeChange(affiliates?.currentTotals ?? 0, affiliates?.previousTotals, periodDays, n),
      series: affiliates?.affiliateByDay,
      unit: "affiliate",
    },
    {
      id: "dashboard-map-new-affiliates-card",
      label: "Post-checkout joins",
      value: n(postCheckout?.currentTotals ?? 0),
      change: describeChange(postCheckout?.currentTotals ?? 0, postCheckout?.previousTotals, periodDays, n),
      unit: "join",
    },
  ];

  return (
    <MetricPanel
      title="Activity"
      metrics={metrics}
      columns="lg:grid-cols-3 xl:grid-cols-6"
      className={cn("transition-opacity", isRefreshing && "opacity-60")}
      action={<PeriodToggle value={periodDays} onChange={onPeriodChange} disabled={isRefreshing} />}
    />
  );
}
